import {
    ReportingHandlerConfig,
    ReportingHandlerProps,
    ReportingMessageHandler,
} from './handlerInterface';
import { ContextItem, ThreadContextItem } from '@varos/assistant-sdk';
import { assertNever } from '@varos/util-typescript';

export function createAskHandler(
    config: ReportingHandlerConfig
): ReportingMessageHandler<'ask'> {
    return async (props: ReportingHandlerProps<'ask'>) => {
        const { event, mutations, chatState } = props;
        const { text, currentThread, content, selection } = event.data;

        // If currentThread is true, use the existing thread
        if (currentThread) {
            console.log(
                'ask was called with currentThread, doing nothing for now',
                chatState.value.thread
            );
            // // Create a message in the current thread
            // const message = await mutations.messageCreate.mutateAsync({
            //     thread: chatState.value.thread!,
            //     parts: [
            //         {
            //             type: 'text',
            //             text: text,
            //         },
            //     ],
            //     context: [
            //         {
            //             kind: 'root',
            //             data: {
            //                 document: {
            //                     id: content.id,
            //                 },
            //             },
            //         },
            //     ],
            // });
            // console.log('message created in current thread', message);
            return;
        }

        console.log('creating thread');
        const threadStartContext: ThreadContextItem[] = [
            {
                kind: 'selection',
                document: content.id,
                blocks: event.data.selection.blocks.map((block) => block.id),
                start: {
                    line: event.data.selection.start.lineNumber,
                    column: event.data.selection.start.column,
                },
                end: {
                    line: event.data.selection.end.lineNumber,
                    column: event.data.selection.end.column,
                },
                plaintext: event.data.selection.plaintext,
            },
        ];
        const thread = await mutations.threadCreate.mutateAsync({
            title: 'New Ask Thread',
            context: threadStartContext,
        });
        // now open the chat detail page
        chatState.onChange({
            thread: thread.id,
            isOpen: true,
            assistantMode: 'ask',
            state: 'preparing',
        });
        // Create the thread
        console.log('thread created', thread);

        try {
            // Generate thread suggestion based on the ask text
            const threadSuggestion = await mutations.threadGenerate.mutateAsync({
                prompt: text,
                mode: 'ask',
                context: [
                    {
                        kind: 'selection',
                        data: {
                            document: content.id,
                            blocks: event.data.selection.blocks.map((block) => block.id),
                            start: {
                                line: event.data.selection.start.lineNumber,
                                column: event.data.selection.start.column,
                            },
                            end: {
                                line: event.data.selection.end.lineNumber,
                                column: event.data.selection.end.column,
                            },
                            plaintext: event.data.selection.plaintext,
                        },
                    },
                ],
            });

            console.log('thread suggestion', threadSuggestion);

            console.log('updating thread');
            await mutations.threadUpdate.mutateAsync([
                thread.id,
                {
                    title: threadSuggestion.title,
                    context: [...threadSuggestion.context],
                },
            ]);

            // Enqueue the message on the state
            console.log('enqueuing message');
            chatState.onChange({
                thread: thread.id,
                isOpen: true,
                assistantMode: 'ask',
                state: 'active',
                enqueuedMessages: [
                    {
                        idempotencyKey: new Date().getTime().toString(),
                        parts: [
                            {
                                type: 'text',
                                text: text,
                            },
                        ],
                        context: threadSuggestion.context.map((item): ContextItem => {
                            if (item.kind === 'prompt') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        text: item.prompt,
                                    },
                                };
                            }
                            if (item.kind === 'root' || item.kind === 'reference') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        document: item.document,
                                    },
                                };
                            }
                            if (item.kind === 'task') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        goal: item.goal,
                                        steps: item.steps,
                                    },
                                };
                            }
                            if (item.kind === 'form') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        formId: item.formId,
                                    },
                                };
                            }
                            if (item.kind === 'template') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        template: item.template,
                                    },
                                };
                            }
                            if (item.kind === 'collection') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        collectionId: item.collectionId,
                                    },
                                };
                            }
                            if (item.kind === 'file') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        filename: item.filename,
                                        type: item.type,
                                        content: item.content,
                                    },
                                };
                            }
                            if (item.kind === 'resource') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        resource_id: item.resource_id,
                                        namespace: item.namespace,
                                    },
                                };
                            }
                            if (item.kind === 'selection') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        document: item.document,
                                        blocks: item.blocks,
                                        start: item.start,
                                        end: item.end,
                                        plaintext: item.plaintext,
                                    },
                                };
                            }
                            if (item.kind === 'solution') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        solutionId: item.solutionId,
                                    },
                                };
                            }
                            if (item.kind === 'integration') {
                                return {
                                    kind: item.kind,
                                    data: {
                                        integrationId: item.integrationId,
                                    },
                                };
                            }
                            assertNever(item);
                        }),
                    },
                ],
            });
            console.log('message enqueued');
        } catch (error) {
            console.error('error preparing thread', error);
            chatState.onChange({
                thread: thread.id,
                isOpen: true,
                assistantMode: 'ask',
                state: 'active',
                enqueuedMessages: null,
            });
            throw error;
        }
    };
}
